import { AITransactionFilter } from "@/components/AITransactionFilter";
import { CreateTransactionSheet } from "@/components/CreateTransactionSheet";
import { InlineTransactionTable } from "@/components/InlineTransactionTable";
import { TransactionFilters } from "@/components/TransactionFilters";
import { TransactionSortSelector } from "@/components/TransactionSortSelector";
import { UpdateTransactionSheet } from "@/components/UpdateTransactionSheet";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { DateRangePicker, type DateRange } from "@/app/shared/ui/date-range-picker";
import { Pagination } from "@/app/shared/ui/pagination";
import { useAccounts, useCategories, useTags, useTransaction, useTransactions } from "@/hooks/api";
import { formatDate } from "@/lib/date.utils";
import { AccountSidebar } from "@/app/layout/AccountSidebar";
import { AccountType, TransactionType, type TransactionFilters as FilterType } from "@/types";
import dayjs from "dayjs";
import { Plus, Search, X } from "lucide-react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { useEffect, useMemo, useState } from "react";

interface CardTransactionsTabProps {
  month: string;
}

/**
 * Credit-card transactions register — mirrors the /transactions page exactly
 * (toolbar, filters, search, sort, edit sheet, pinned pagination) but pinned
 * to credit-card accounts and the selected billing month's union window.
 */
export function CardTransactionsTab({ month }: CardTransactionsTabProps) {
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isAddingInline, setIsAddingInline] = useState(false);

  const [queryParams, setQueryParams] = useQueryStates({
    id: parseAsInteger,
    startDate: parseAsString,
    endDate: parseAsString,
    accountId: parseAsInteger,
    categoryId: parseAsInteger,
    type: parseAsString,
    isReviewed: parseAsString,
    minAmount: parseAsInteger,
    maxAmount: parseAsInteger,
    search: parseAsString,
    sortBy: parseAsString.withDefault("date"),
    sortOrder: parseAsString.withDefault("desc"),
    page: parseAsInteger.withDefault(1),
  });

  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const { data: tags = [] } = useTags();
  const { data: transactionDetail } = useTransaction(queryParams.id || 0);

  const activeAccounts = accounts.filter((account) => !account.isArchived && !account.isDeleted);
  const cardAccounts = useMemo(
    () => activeAccounts.filter((a) => a.type === AccountType.CREDIT_CARD),
    [activeAccounts],
  );
  const selectedCard = queryParams.accountId
    ? cardAccounts.find((a) => a.id === queryParams.accountId)
    : null;

  // Union window across all cards' statement-day cycles for this billing month
  // (statement day D of month M covers D+1 of M-1 .. D of M, clamped; cards
  // without a statement day fall back to the calendar month).
  const { startDate, endDate } = useMemo(() => {
    const [year, monthIdx] = month.split("-").map(Number);
    const monthStart = new Date(year, monthIdx - 1, 1);
    const monthAfter = new Date(year, monthIdx, 1);
    if (cardAccounts.length === 0) {
      return { startDate: formatDate(monthStart), endDate: formatDate(monthAfter) };
    }

    let minStart = monthAfter;
    let maxEnd = monthStart;
    for (const card of cardAccounts) {
      const sd = card.statementDayOfMonth;
      if (!sd) {
        if (monthStart < minStart) minStart = monthStart;
        if (monthAfter > maxEnd) maxEnd = monthAfter;
        continue;
      }
      const daysInMonth = new Date(year, monthIdx, 0).getDate();
      const clamped = Math.min(sd, daysInMonth);
      const statementDate = new Date(year, monthIdx - 1, clamped);
      const prevMonthDays = new Date(year, monthIdx - 1, 0).getDate();
      const prevDay = Math.min(sd, prevMonthDays);
      const start =
        prevDay + 1 > prevMonthDays ? monthStart : new Date(year, monthIdx - 2, prevDay + 1);
      if (start < minStart) minStart = start;
      if (statementDate > maxEnd) maxEnd = statementDate;
    }
    return { startDate: formatDate(minStart), endDate: formatDate(maxEnd) };
  }, [cardAccounts, month]);

  const PAGE_SIZE = 50;
  const currentPage = queryParams.page || 1;

  const filters: FilterType = {
    startDate: queryParams.startDate || startDate,
    endDate: queryParams.endDate || endDate,
    accountId: queryParams.accountId || undefined,
    categoryId: queryParams.categoryId || undefined,
    accountType: AccountType.CREDIT_CARD,
    type:
      queryParams.type === "credit" || queryParams.type === "debit"
        ? (queryParams.type as TransactionType)
        : undefined,
    isReviewed:
      queryParams.isReviewed === "true"
        ? true
        : queryParams.isReviewed === "false"
          ? false
          : undefined,
    minAmount: queryParams.minAmount || undefined,
    maxAmount: queryParams.maxAmount || undefined,
    search: queryParams.search || undefined,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    sortBy: (queryParams.sortBy as "date" | "amount" | "createdAt") || "date",
    sortOrder: (queryParams.sortOrder as "asc" | "desc") || "desc",
  };

  const { data: transactionsData, isLoading } = useTransactions(filters);
  const transactions = transactionsData?.transactions || [];
  const total = transactionsData?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleFiltersChange = (newFilters: FilterType) => {
    setQueryParams({
      accountId: newFilters.accountId || null,
      categoryId: newFilters.categoryId || null,
      type: newFilters.type || null,
      isReviewed: newFilters.isReviewed !== undefined ? String(newFilters.isReviewed) : null,
      minAmount: newFilters.minAmount || null,
      maxAmount: newFilters.maxAmount || null,
      page: 1,
    });
  };

  const handleDateRangeChange = (dateRange?: DateRange) => {
    if (!dateRange?.from || !dateRange?.to) return;
    setQueryParams({
      startDate: formatDate(dateRange.from),
      endDate: formatDate(dateRange.to),
      page: 1,
    });
  };

  const handleSortChange = (newSortOrder: "asc" | "desc") => {
    setQueryParams({ sortOrder: newSortOrder, page: 1 });
  };

  const handleColumnSort = (sortBy: "date" | "amount", sortOrder: "asc" | "desc") => {
    setQueryParams({ sortBy, sortOrder, page: 1 });
  };

  // Debounced search (Actual-style)
  const [searchInput, setSearchInput] = useState(queryParams.search || "");
  useEffect(() => {
    setSearchInput(queryParams.search || "");
  }, [queryParams.search]);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (queryParams.search || "")) return;
    const timeout = setTimeout(() => {
      setQueryParams({ search: trimmed || null, page: 1 });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, queryParams.search, setQueryParams]);

  const handleAddNew = () => {
    if (window.matchMedia("(min-width: 768px)").matches) {
      setIsAddingInline(true);
    } else {
      setIsCreateSheetOpen(true);
    }
  };

  return (
    <div className="flex items-start bg-background">
      <AccountSidebar
        filterType={AccountType.CREDIT_CARD}
        allLabel="All Cards"
        allTo="/credit-cards?tab=transactions"
      />
      <div className="min-w-0 flex-1">
        <div className="flex h-full flex-col overflow-hidden">
          {/* Header Bar */}
          <div className="border-b border-border px-4 py-4 md:px-6">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-lg font-medium text-foreground">
                  {selectedCard ? selectedCard.accountName : "Card Transactions"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {selectedCard ? "Single card" : "All cards"} ·{" "}
                  {formatDate(filters.startDate || startDate)} →{" "}
                  {formatDate(filters.endDate || endDate)}
                </p>
              </div>

              <div className="relative w-40 flex-shrink-0 sm:w-56">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search"
                  className="h-8 pr-7 pl-8 text-sm"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={handleAddNew}>
                <Plus className="size-3.5" />
                Add New
              </Button>
              <TransactionFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                accounts={cardAccounts}
                categories={categories}
              />
              <div className="flex w-full gap-2 sm:ml-auto sm:w-auto">
                <div className="md:hidden">
                  <TransactionSortSelector
                    value={filters.sortOrder || "desc"}
                    onValueChange={handleSortChange}
                  />
                </div>
                <AITransactionFilter
                  onFilterApply={handleFiltersChange}
                  accounts={cardAccounts}
                  categories={categories}
                />
                <DateRangePicker
                  className="w-full sm:w-auto"
                  value={{
                    from: filters.startDate ? dayjs(filters.startDate).toDate() : undefined,
                    to: filters.endDate ? dayjs(filters.endDate).toDate() : undefined,
                  }}
                  onChange={handleDateRangeChange}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <InlineTransactionTable
              transactions={transactions}
              accounts={cardAccounts}
              categories={categories}
              tags={tags}
              isLoading={isLoading}
              onEdit={(transaction) => setQueryParams({ id: transaction.id })}
              startDate={filters.startDate}
              endDate={filters.endDate}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSort={handleColumnSort}
              isAdding={isAddingInline}
              onCancelAdd={() => setIsAddingInline(false)}
              defaultAccountId={queryParams.accountId || undefined}
            />
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="border-t border-border px-4 py-3 md:px-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={(newPage) => setQueryParams({ page: newPage })}
                isLoading={isLoading}
                itemName="transactions"
              />
            </div>
          )}
        </div>

        <CreateTransactionSheet
          accounts={cardAccounts}
          categories={categories}
          tags={tags}
          isOpen={isCreateSheetOpen}
          onOpenChange={setIsCreateSheetOpen}
          defaultAccountId={queryParams.accountId || undefined}
        />

        <UpdateTransactionSheet
          transaction={transactionDetail || null}
          accounts={activeAccounts}
          categories={categories}
          tags={tags}
          isOpen={!!queryParams.id}
          onOpenChange={(open) => {
            if (!open) setQueryParams({ id: null });
          }}
          onUpdate={() => {
            setQueryParams({ id: null });
          }}
        />
      </div>
    </div>
  );
}
